import { create } from 'zustand'
import type { StatusDialogsResult } from 'payments/activate-trial-button/components/activate-trial-status-dialogs'
import { PaymentIntentStatus, PaymentStatus } from '@repo/graphql-types'
interface PaymentRequiredDialogStore {
  /**
   * Hosted Stripe invoice URL returned by the backend when a payment intent
   * requires action (e.g. `COIN_ERROR_CODE_PAYMENT_REQUIRED` from Studio
   * Matrix RPCs, or a `requiresAction` status from the coin subscription /
   * plan-change mutations). When truthy, the `PaymentRequiredDialog` rendered
   * in the host app's `AppDialogs` is open. Clearing it closes the dialog.
   */
  result: StatusDialogsResult | null
}

export const usePaymentRequiredDialogStore = create<PaymentRequiredDialogStore>(() => ({
  result: null,
}))

export function openPaymentRequiredDialog(result: StatusDialogsResult) {
  usePaymentRequiredDialogStore.setState({ result })
}

export function closePaymentRequiredDialog() {
  usePaymentRequiredDialogStore.setState({ result: null })
}

export function mapIntentStatusToPaymentStatus(intentStatus: PaymentIntentStatus): PaymentStatus {
  switch (intentStatus) {
    case PaymentIntentStatus.RequiresAction:
    case PaymentIntentStatus.RequiresConfirmation:
    case PaymentIntentStatus.RequiresPaymentMethod:
    case PaymentIntentStatus.RequiresCapture: {
      return PaymentStatus.RequiresAction
    }
    case PaymentIntentStatus.Succeeded: {
      return PaymentStatus.Succeeded
    }
    case PaymentIntentStatus.Processing: {
      return PaymentStatus.Processing
    }
    default: {
      return PaymentStatus.Failed
    }
  }
}

export function mapCoinPaymentIntentStatusToPaymentStatus(status?: string): PaymentStatus {
  switch (status) {
    case 'COIN_PAYMENT_INTENT_STATUS_SUCCEEDED': {
      return PaymentStatus.Succeeded
    }
    case 'COIN_PAYMENT_INTENT_STATUS_PROCESSING': {
      return PaymentStatus.Processing
    }
    case 'COIN_PAYMENT_INTENT_STATUS_REQUIRES_ACTION': {
      return PaymentStatus.RequiresAction
    }
    default: {
      return PaymentStatus.RequiresAction
    }
  }
}

export function buildStatusDialogsResultFromPaymentStatus(
  paymentStatus: PaymentStatus,
  hostedUrl: string | null,
): StatusDialogsResult {
  return {
    error: null,
    message: hostedUrl,
    paymentsStatus: paymentStatus,
    userBanned: false,
  }
}

export function buildStatusDialogsResultFromStripeInvoicePayUrl(
  stripeInvoicePayUrl: string | null,
  paymentIntentStatus?: string,
): StatusDialogsResult {
  return buildStatusDialogsResultFromPaymentStatus(
    mapCoinPaymentIntentStatusToPaymentStatus(paymentIntentStatus),
    stripeInvoicePayUrl,
  )
}

type SubscriptionPaymentIntentInfo =
  | {
      latestInvoice?: {
        hostedUrl?: string | null
        paymentIntent?: { status?: PaymentIntentStatus | null } | null
      } | null
    }
  | null
  | undefined

/**
 * True when a coin-subscription mutation's latest payment intent is present
 * and did not succeed. Used by derived render state (e.g. `successPlan`) so
 * a requires-action response stays `null` and the underlying top-up dialog
 * doesn't close out from under the recovery dialog stacked on top of it.
 */
export function subscriptionRequiresPaymentRecovery(
  subscription: SubscriptionPaymentIntentInfo,
): boolean {
  const intentStatus = subscription?.latestInvoice?.paymentIntent?.status
  if (!intentStatus) {
    return false
  }
  return mapIntentStatusToPaymentStatus(intentStatus) !== PaymentStatus.Succeeded
}

/**
 * Opens the shared `PaymentRequiredDialog` with the hosted Stripe invoice URL
 * when a coin-subscription mutation's latest payment intent didn't succeed.
 * Returns `true` so callers can short-circuit their own success path (STU-7812).
 */
export function tryOpenPaymentRequiredDialogFromSubscription(
  subscription: SubscriptionPaymentIntentInfo,
): boolean {
  if (!subscriptionRequiresPaymentRecovery(subscription)) {
    return false
  }
  const intentStatus = subscription?.latestInvoice?.paymentIntent?.status
  const hostedUrl = subscription?.latestInvoice?.hostedUrl ?? null
  // Safe non-null assertion: `subscriptionRequiresPaymentRecovery` returns
  // false when `intentStatus` is falsy, so reaching this line guarantees it.
  const paymentStatus = mapIntentStatusToPaymentStatus(intentStatus!)
  const result = buildStatusDialogsResultFromPaymentStatus(paymentStatus, hostedUrl)
  openPaymentRequiredDialog(result)
  return true
}
